import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { spacesApi } from '../../../api/spacesApi/spacesAPIclient'
import { IWhitelistedUser } from '@valuecase/common'

interface UseSpaceWhitelistedUsersOptions {
  enabled?: boolean
  staleTime?: number
  includeNotificationSettings?: boolean
}

/**
 * Hook to fetch whitelisted users for a specific space
 * @param rootNodeId - The rootNodeId of the space to fetch whitelisted users for
 * @param options - Additional options for the query
 * @returns Query result with whitelisted users data, loading state, and error
 */
export const useSpaceWhitelistedUsers = (
  rootNodeId?: string,
  options: UseSpaceWhitelistedUsersOptions = {},
) => {
  const { enabled = true, includeNotificationSettings = true, staleTime = 1000 * 60 } = options

  const queryKey = useMemo(
    () => ['spaceWhitelistedUsers', rootNodeId, includeNotificationSettings],
    [rootNodeId, includeNotificationSettings],
  )

  return useQuery<IWhitelistedUser[]>({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!rootNodeId) return []

      const response = await spacesApi.get(`/whitelisted-users/space/${rootNodeId}`, {
        params: { includeNotificationSettings },
        signal,
      })

      return response.data
    },
    enabled: enabled && !!rootNodeId,
    staleTime,
  })
}
